import { ArrowRight } from '@drift-labs/icons';
import { useContext, useEffect } from 'react';
import XButton from 'src/components/Buttons/XButton';
import Utility from 'src/components/Inputs/Utility';
import { SettingsClientContext } from 'src/providers/settingsProvider';
import useDriftStore, { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import {
	NotificationData,
	TIMEOUT_NOTIFICATION_DATA,
	notify,
} from 'src/utils/notifications';
import UI_UTILS from '../../utils/uiUtils';
import { GradientButton } from '../Button';
import CheckboxInput from '../CheckboxInput';
import Toast from './Toast';

const TIMEOUT_FAILSAFE_MS = 50_000;

const Notification = ({
	index,
	notificationData,
	statusWarning,
	statusType,
}: {
	index: number;
	notificationData: NotificationData;
	statusWarning: DriftStore['driftStatus']['mainStatus']['message'];
	statusType: DriftStore['driftStatus']['mainStatus']['status'];
}) => {
	const setStore = useDriftStore((s) => s.set);
	const notifications = useDriftStore((s) => s.notifications);
	const settingsContext = useContext(SettingsClientContext);

	const targetIgnoreToastEnabled =
		notificationData.offerIgnoreToast &&
		settingsContext.currentSettings.ignoreToasts[
			notificationData.offerIgnoreToast
		];

	const handleToggleTargetIgnoreToast = () => {
		settingsContext.settingsClient.updateSettings({
			ignoreToasts: {
				...settingsContext.currentSettings.ignoreToasts,
				[notificationData.offerIgnoreToast]:
					!settingsContext.settingsClient.overallSettingsState[
						notificationData.offerIgnoreToast
					],
			},
		});
	};

	const closeNotification = () => {
		setStore((state) => {
			const newNotifications = [...notifications].reverse();

			newNotifications.splice(index, 1);

			state.notifications = newNotifications.reverse();
		});
	};

	const setHovered = () => {
		setStore((s) => {
			s.notifications[index].isHovered = true;
		});
	};

	const unsetHovered = () => {
		setStore((s) => {
			s.notifications[index].isHovered = false;
		});
	};

	const {
		url,
		txid,
		message,
		description,
		subDescription,
		type,
		customIcon,
		customIconSizeOverride,
		timeoutIfProlongedTxn: timeoutForProlongedTxnFailSafe,
	} = notificationData;

	// this is a failsafe to ensure that if a transaction is taking too long, we update
	// the toast to inform the user that the transaction may have timed out
	useEffect(() => {
		if (timeoutForProlongedTxnFailSafe) {
			const timeout = setTimeout(() => {
				notify({
					...notificationData,
					...TIMEOUT_NOTIFICATION_DATA,
					timeoutIfProlongedTxn: false,
				});
			}, TIMEOUT_FAILSAFE_MS);
			return () => clearTimeout(timeout);
		}
	}, [timeoutForProlongedTxnFailSafe]);

	const handleClick = () => {
		if (!url && !txid) return;

		if (txid) {
			window.open(UI_UTILS.getUrlForTx(txid), '_blank');
		}

		if (url) {
			window.open(url, '_blank');
		}
	};

	const colourTypeToUse = notificationData.bgType
		? notificationData.bgType
		: notificationData.type;

	return (
		<Toast
			onMouseEnter={setHovered}
			onMouseLeave={unsetHovered}
			onClick={handleClick}
			type={colourTypeToUse}
		>
			<div className="flex">
				<div className="flex flex-col w-full space-y-1">
					<div className={`flex items-center space-x-4`}>
						<div
							className="relative flex-shrink-0"
							style={{
								width: customIconSizeOverride ? customIconSizeOverride : '26px',
								height: customIconSizeOverride
									? customIconSizeOverride
									: '26px',
							}}
						>
							<div className="absolute top-0 flex items-center justify-center w-full h-full z-1">
								{customIcon ? (
									customIcon
								) : (
									<>
										{type === 'info' && <Toast.InfoIcon />}
										{type === 'warning' && <Toast.InfoIcon />}
										{type === 'error' && <Toast.ErrorIcon />}
										{type === 'success' && <Toast.SuccessIcon />}
										{type === 'awaiting' && <Toast.LoadingIcon />}
										{type === 'points' && <Toast.PointsIcon />}
									</>
								)}
							</div>
						</div>
						<div
							className={`text-lg relative top-0.5 w-full text-static-emphasized`}
						>
							{message}
						</div>
						<div className="flex justify-between ml-2">
							<XButton
								stroke="var(--neutrals-40)"
								onClick={(e) => {
									e.stopPropagation();
									closeNotification();
								}}
							/>
						</div>
					</div>

					{(description || url || txid || subDescription) && (
						<Utility.VERTSPACERM />
					)}

					<div className="flex w-full">
						{notificationData.leftPadContent && (
							<div
								// 16px is the gap size between title and icon
								style={{ width: (customIconSizeOverride ?? 26) + 16 }}
								className="shrink-0"
							>
								{/** Faux spacing */}
							</div>
						)}

						<div className="flex flex-col w-full space-y-1">
							{(description || url || txid || subDescription) && (
								<>
									{description && (
										<div className="flex items-center space-x-4 text-sm text-static-default">
											{/* <LeftSpacer /> */}
											<div className={`flex flex-grow space-y-2 break-word`}>
												{description}
											</div>
										</div>
									)}

									{subDescription && (
										<div className="flex items-center space-x-4 text-xs text-static-default">
											{/* <LeftSpacer /> */}
											<div className={`flex flex-grow space-y-2 break-word`}>
												{subDescription}
											</div>
										</div>
									)}

									{txid && (
										<div className="flex items-center space-x-4 text-xs text-static-default">
											{/* <LeftSpacer /> */}
											<div className={`flex flex-grow space-y-2`}>
												<div className="text-xs text-yellow-50 hover:text-neutrals-30 hover:underline">
													Click here to view transaction {txid.slice(0, 4)}
													...
													{txid.slice(txid.length - 4)}
												</div>
											</div>
										</div>
									)}
								</>
							)}

							{notificationData.action && (
								<>
									{' '}
									<Utility.VERTSPACERXS />
									<div
										onClick={(e) => {
											notificationData.action.callback();
											closeNotification();
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										{notificationData?.action?.type === 'rainbow-button' ? (
											<>
												<GradientButton size="MEDIUM">
													{notificationData.action.label}
												</GradientButton>
											</>
										) : notificationData?.action?.type === 'prize-button' ? (
											<>
												<GradientButton type="prize" size="MEDIUM">
													{notificationData.action.label}
												</GradientButton>
											</>
										) : notificationData?.action?.type === 'points-button' ? (
											<>
												<GradientButton type="points" size="MEDIUM">
													{notificationData.action.label}
												</GradientButton>
											</>
										) : (
											<>
												<div
													className="flex items-center space-x-2 hover:brightness-125 hover:cursor-pointer"
													onClick={(e) => {
														notificationData.action.callback();
														closeNotification();
														e.preventDefault();
														e.stopPropagation();
													}}
												>
													<div className="text-text-default">
														{notificationData.action.label}
													</div>
													<ArrowRight size={14} />
												</div>
											</>
										)}
									</div>
								</>
							)}

							{notificationData.bottomContent ? (
								<>
									<Utility.VERTSPACERS />
									{notificationData.bottomContent}
								</>
							) : (
								<></>
							)}

							{statusWarning && (
								<>
									{' '}
									<Utility.VERTSPACERM />
									<div className="flex items-center space-x-4 text-xs text-neutrals-40">
										<div
											className="relative flex items-center justify-center flex-shrink-0"
											style={{ width: '26px', height: '10px' }}
										>
											<div
												className={`w-2 h-2 rounded-full ${
													statusType === 'green'
														? 'bg-positive-green'
														: statusType === 'amber'
														? 'bg-warn-yellow'
														: 'bg-negative-red'
												}`}
											/>
										</div>
										<div className={`flex flex-grow space-y-2`}>
											{statusWarning}
										</div>
									</div>
								</>
							)}

							{url && (
								<>
									{' '}
									<Utility.VERTSPACERXS />
									<div className="flex items-center space-x-2 hover:brightness-125">
										<div className="text-text-default">More Info</div>
										<ArrowRight size={14} />
									</div>
								</>
							)}

							{notificationData.offerIgnoreToast && (
								<>
									{' '}
									<Utility.VERTSPACERXS />
									<div
										className="flex items-left"
										onClick={(e) => {
											// Things like attached URLs work if any click event bubbles up, we want to prevent that for this part of the toast.
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										<CheckboxInput
											label="Don't show again"
											checked={targetIgnoreToastEnabled}
											onChange={handleToggleTargetIgnoreToast}
											secondaryStyle
										/>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</Toast>
	);
};

export default Notification;
